import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { getPlayoffBonusColumns } from './playoffScoring';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(1),
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
}));

const PoolHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#2E4156',
  color: '#ffffff',
  padding: theme.spacing(1.5, 2),
  borderRadius: '4px 4px 0 0',
  fontWeight: 'bold',
}));

const MobileStandingCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  border: '1px solid rgba(0, 0, 0, 0.12)',
}));

const PositionBadge = styled('div')(({ rank }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: rank <= 2 ? '#2E4156' : '#757575',
  color: '#fff',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  marginRight: 8,
}));

const FormIndicator = ({ result }) => {
  const getColor = (r) => {
    switch (r.toUpperCase()) {
      case 'W': return '#4caf50';
      case 'D': return '#ff9800';
      case 'L': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        borderRadius: '10px',
        backgroundColor: getColor(result),
        color: 'white',
        fontSize: '0.65rem',
        fontWeight: 'bold',
        margin: '1px'
      }}
    >
      {result.toUpperCase()}
    </Box>
  );
};

const MobilePoolStandings = ({ standings, bonusColumns }) => {
  return (
    <Box>
      {standings.map((team) => (
        <MobileStandingCard key={team.teamId}>
          <CardContent sx={{ padding: 2, '&:last-child': { paddingBottom: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PositionBadge rank={team.position}>
                  {team.position}
                </PositionBadge>
                <Typography variant="body1" fontWeight="bold">
                  {team.teamName}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', lineHeight: 1 }}>
                  {team.totalPoints}
                </Typography>
                <Typography variant="caption" color="text.secondary">pts</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
              {[
                { label: 'P', value: team.matchesPlayed },
                { label: 'W', value: team.wins, color: 'success.main' },
                { label: 'D', value: team.draws, color: 'warning.main' },
                { label: 'L', value: team.losses, color: 'error.main' },
                { label: 'GF', value: team.goalsFor },
                { label: 'GA', value: team.goalsAgainst },
                { label: 'GD', value: team.goalDifference, color: team.goalDifference > 0 ? 'success.main' : team.goalDifference < 0 ? 'error.main' : undefined },
                bonusColumns.goalBonus && { label: 'GB', value: team.goalBonusPoints },
                bonusColumns.shutout && { label: 'SO', value: team.shutoutPoints },
              ].filter(Boolean).map(({ label, value, color }) => (
                <Box key={label} sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1, color: color || 'text.primary' }}>
                    {label === 'GD' && value > 0 ? '+' : ''}{value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Box>
              ))}
            </Box>

            {team.recentForm && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, gap: 0.5 }}>
                {team.recentForm.split('').slice(-5).reverse().map((r, i) => (
                  <FormIndicator key={i} result={r} />
                ))}
              </Box>
            )}
          </CardContent>
        </MobileStandingCard>
      ))}
    </Box>
  );
};

const PlayoffPoolStandings = ({ pool, scoring }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const bonusColumns = getPlayoffBonusColumns(scoring);

  if (!pool || !pool.standings || pool.standings.length === 0) {
    return null;
  }

  const renderFormCells = (recentForm) => {
    if (!recentForm || recentForm.length === 0) {
      return <TableCell align="center">-</TableCell>;
    }
    const form = recentForm.length >= 5 ? recentForm.slice(-5) : recentForm.padStart(5, '-');
    const formArray = form.split('').reverse();
    return (
      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
        {formArray.map((r, i) => (
          r === '-' ? <Box key={i} component="span" sx={{ mx: 0.25, color: 'text.disabled' }}>-</Box> : <FormIndicator key={i} result={r} />
        ))}
      </TableCell>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      <PoolHeader>
        <Typography variant="subtitle1" fontWeight="bold">
          {pool.poolName} Standings
        </Typography>
      </PoolHeader>

      {isMobile ? (
        <MobilePoolStandings standings={pool.standings} bonusColumns={bonusColumns} />
      ) : (
        <StyledTableContainer component={Paper} sx={{ borderRadius: '0 0 4px 4px' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell align="center" sx={{ width: 40, fontWeight: 'bold' }}>Pos</TableCell>
                <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }}>Team</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>P</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>W</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>D</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>L</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>GF</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>GA</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>GD</TableCell>
                {bonusColumns.goalBonus && <TableCell align="center" sx={{ fontWeight: 'bold' }}>Goal Bonus</TableCell>}
                {bonusColumns.shutout && <TableCell align="center" sx={{ fontWeight: 'bold' }}>Shutout</TableCell>}
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pts</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Form</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pool.standings.map((team) => (
                <TableRow
                  key={team.teamId}
                  hover
                  sx={{
                    '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                  }}
                >
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>{team.position}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <Link
                      to={`/teams/${team.teamId}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      {team.teamName}
                    </Link>
                  </TableCell>
                  <TableCell align="center">{team.matchesPlayed}</TableCell>
                  <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold' }}>{team.wins}</TableCell>
                  <TableCell align="center" sx={{ color: 'warning.main', fontWeight: 'bold' }}>{team.draws}</TableCell>
                  <TableCell align="center" sx={{ color: 'error.main', fontWeight: 'bold' }}>{team.losses}</TableCell>
                  <TableCell align="center">{team.goalsFor}</TableCell>
                  <TableCell align="center">{team.goalsAgainst}</TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      color: team.goalDifference > 0 ? 'success.main' :
                             team.goalDifference < 0 ? 'error.main' : 'text.primary'
                    }}
                  >
                    {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}
                  </TableCell>
                  {bonusColumns.goalBonus && <TableCell align="center">{team.goalBonusPoints}</TableCell>}
                  {bonusColumns.shutout && <TableCell align="center">{team.shutoutPoints}</TableCell>}
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'primary.main' }}>
                    {team.totalPoints}
                  </TableCell>
                  {renderFormCells(team.recentForm)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      )}
    </Box>
  );
};

export default PlayoffPoolStandings;
